// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
  Textarea,
  SearchableSelect,
} from '@/components/ui';
import { Loader2 } from 'lucide-react';
import {
  fetchOrganizations,
  fetchOrgWorkspaces,
  type OrganizationResponse,
  type WorkspaceInfo
} from '@/subsystems/workspaces';
import { CadenceField } from './CadenceField';
import type { CreateSchedulerRequest } from '../api/types/scheduler';

interface CreateSchedulerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateSchedulerRequest) => Promise<void>;
}

export function CreateSchedulerDialog({
  open,
  onOpenChange,
  onSubmit,
}: CreateSchedulerDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<CreateSchedulerRequest>({
    job_name: '',
    cadence: 'daily',
    org: null,
    scope: null,
    ws: null,
    args: null,
  });
  const [argsText, setArgsText] = useState('');
  const [argsError, setArgsError] = useState('');

  // Fetch organizations
  const { data: allOrgs = [] } = useQuery<OrganizationResponse[]>({
    queryKey: ['organizations'],
    queryFn: fetchOrganizations,
    enabled: open,
  });

  // Fetch workspaces for selected org
  const { data: allWorkspaces = [] } = useQuery<WorkspaceInfo[]>({
    queryKey: ['workspaces', formData.org],
    queryFn: () => fetchOrgWorkspaces(formData.org!),
    enabled: open && !!formData.org,
  });

  // Organization options
  const orgOptions = useMemo(() => {
    return allOrgs.map((org: OrganizationResponse) => ({
      value: org.orgId,
      label: `${org.name} (${org.orgId})`
    }));
  }, [allOrgs]);

  // Workspace options
  const workspaceOptions = useMemo(() => {
    return allWorkspaces.map((ws: WorkspaceInfo) => ({
      value: ws.wsId,
      label: ws.name ? `${ws.name} (${ws.wsId})` : ws.wsId
    }));
  }, [allWorkspaces]);

  const handleArgsChange = (value: string) => {
    setArgsText(value);
    setArgsError('');

    if (!value.trim()) {
      setFormData((prev) => ({ ...prev, args: null }));
      return;
    }

    try {
      const parsed = JSON.parse(value);
      if (typeof parsed !== 'object' || Array.isArray(parsed)) {
        setArgsError('Arguments must be a JSON object');
        return;
      }
      setFormData((prev) => ({ ...prev, args: parsed }));
    } catch {
      setArgsError('Invalid JSON format');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (argsError) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      // Reset form
      setFormData({
        job_name: '',
        cadence: 'daily',
        org: null,
        scope: null,
        ws: null,
        args: null,
      });
      setArgsText('');
      setArgsError('');
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to create scheduler:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Reset form on cancel
    setFormData({
      job_name: '',
      cadence: 'daily',
      org: null,
      scope: null,
      ws: null,
      args: null,
    });
    setArgsText('');
    setArgsError('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create Scheduled Job</DialogTitle>
          <DialogDescription>
            Configure a new scheduled job to run automatically at specified intervals.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="job_name">Job Name *</Label>
              <Input
                id="job_name"
                value={formData.job_name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, job_name: e.target.value }))
                }
                placeholder="e.g., sync_data, process_documents"
                required
              />
              <p className="text-sm text-gray-500">
                The name of the job type to execute
              </p>
            </div>

            <CadenceField
              value={formData.cadence}
              onChange={(cadence) => setFormData((prev) => ({ ...prev, cadence }))}
            />

            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="org">Organization</Label>
                <SearchableSelect
                  options={orgOptions}
                  value={formData.org || ''}
                  onValueChange={(value) => {
                    setFormData((prev) => ({
                      ...prev,
                      org: value || null,
                      ws: null, // Clear workspace when org changes
                    }));
                  }}
                  placeholder="Select organization (optional)"
                  searchPlaceholder="Search organizations..."
                  emptyMessage="No organizations found"
                />
                <p className="text-sm text-gray-500">
                  Optional: Scope this job to a specific organization
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="scope">Scope</Label>
                <Input
                  id="scope"
                  value={formData.scope || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      scope: e.target.value || null,
                    }))
                  }
                  placeholder="Optional (e.g., federal-bills/119)"
                />
                <p className="text-sm text-gray-500">
                  Optional: Scope identifier for uniqueness
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="ws">Workspace</Label>
                <SearchableSelect
                  options={workspaceOptions}
                  value={formData.ws || ''}
                  onValueChange={(value) => {
                    setFormData((prev) => ({
                      ...prev,
                      ws: value || null,
                    }));
                  }}
                  placeholder={formData.org ? "Select workspace (optional)" : "Select organization first"}
                  searchPlaceholder="Search workspaces..."
                  emptyMessage={formData.org ? "No workspaces found" : "Select an organization first"}
                  disabled={!formData.org}
                />
                <p className="text-sm text-gray-500">
                  Optional: Scope this job to a specific workspace
                </p>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="args">Arguments (JSON)</Label>
              <Textarea
                id="args"
                value={argsText}
                onChange={(e) => handleArgsChange(e.target.value)}
                placeholder='{"key": "value"}'
                rows={4}
                className={argsError ? 'border-red-500' : ''}
              />
              {argsError && (
                <p className="text-sm text-red-500">{argsError}</p>
              )}
              <p className="text-sm text-gray-500">
                Optional JSON object with parameters to pass to the job
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !!argsError}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Scheduler
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
